import React from 'react';
import { Search, Book, MessageCircle, FileQuestion } from 'lucide-react';
import { Link } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

export function Help() {
    const [formData, setFormData] = React.useState({
        firstName: '',
        lastName: '',
        email: '',
        message: ''
    });
    const [status, setStatus] = React.useState<'idle' | 'sending' | 'success' | 'error'>('idle');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('sending');

        try {
            // Directly store support inquiry into Firebase Firestore
            await addDoc(collection(db, 'contacts'), {
                ...formData,
                createdAt: new Date(),
                status: 'unread'
            });

            setStatus('success');
            alert('Message sent successfully! We will get back to you soon.');
            setFormData({ firstName: '', lastName: '', email: '', message: '' });
        } catch (error) {
            console.error('Error saving contact message:', error);
            setStatus('error');
            // Fallback to mailto if Firestore write fails
            const subject = `Support Request from ${formData.firstName} ${formData.lastName}`;
            const body = `From: ${formData.email}\n\nMessage:\n${formData.message}`;
            window.location.href = `mailto:hello@webappdigitalsolutions.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            alert('Could not save automatically. Opening your email client instead.');
        } finally {
            setStatus('idle');
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    return (
        <div className="min-h-screen bg-background py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-12">
                    <h1 className="text-3xl font-bold text-foreground mb-4">How can we help you?</h1>
                    <div className="max-w-xl mx-auto relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Search for answers..."
                            className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mb-16">
                    <Link to="/developer" className="bg-card border border-border p-6 rounded-xl hover:shadow-lg transition-shadow cursor-pointer group">
                        <Book className="h-8 w-8 text-primary mb-4 group-hover:scale-110 transition-transform" />
                        <h3 className="font-semibold text-foreground mb-2">Documentation</h3>
                        <p className="text-sm text-muted-foreground">Detailed guides and API references for developers.</p>
                    </Link>
                    <a href="#faq" className="bg-card border border-border p-6 rounded-xl hover:shadow-lg transition-shadow cursor-pointer group">
                        <FileQuestion className="h-8 w-8 text-primary mb-4 group-hover:scale-110 transition-transform" />
                        <h3 className="font-semibold text-foreground mb-2">FAQ</h3>
                        <p className="text-sm text-muted-foreground">Common questions about pricing, features, and plans.</p>
                    </a>
                    <a href="#contact" className="bg-card border border-border p-6 rounded-xl hover:shadow-lg transition-shadow cursor-pointer group">
                        <MessageCircle className="h-8 w-8 text-primary mb-4 group-hover:scale-110 transition-transform" />
                        <h3 className="font-semibold text-foreground mb-2">Support</h3>
                        <p className="text-sm text-muted-foreground">Contact our support team for specific issues.</p>
                    </a>
                </div>

                <div id="faq" className="mb-16 scroll-mt-24">
                    <h2 className="text-2xl font-bold text-foreground mb-8">Frequently Asked Questions</h2>
                    <div className="space-y-6">
                        <div className="bg-card border border-border rounded-lg p-6">
                            <h3 className="font-semibold text-foreground mb-2">Is the image data secure?</h3>
                            <p className="text-muted-foreground text-sm">Yes, we prioritize security. Images are processed securely and automatically deleted from our servers after processing.</p>
                        </div>
                        <div className="bg-card border border-border rounded-lg p-6">
                            <h3 className="font-semibold text-foreground mb-2">Can I cancel my subscription?</h3>
                            <p className="text-muted-foreground text-sm">Absolutely. You can cancel your subscription at any time from your dashboard settings.</p>
                        </div>
                        <div className="bg-card border border-border rounded-lg p-6">
                            <h3 className="font-semibold text-foreground mb-2">Do you offer refunds?</h3>
                            <p className="text-muted-foreground text-sm">We offer a 14-day money-back guarantee if you are not satisfied with our service.</p>
                        </div>
                    </div>
                </div>

                <div id="contact" className="bg-card border border-border rounded-xl p-8 scroll-mt-24">
                    <h2 className="text-2xl font-bold text-foreground mb-8">Contact Us</h2>
                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="font-semibold text-foreground mb-4">Get in touch</h3>
                            <p className="text-muted-foreground mb-6">
                                Have a question that isn't answered in our FAQ? Our team is here to help.
                            </p>
                            <div className="space-y-4">
                                <div className="flex items-center gap-3 text-muted-foreground">
                                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <Book className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-foreground">Office Address</p>
                                        <p className="text-sm">Thakur Complex, Kandivali East, Mumbai 400101, India</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 text-muted-foreground">
                                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <MessageCircle className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-foreground">WhatsApp Support</p>
                                        <a href="https://wa.me/918169312050" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-primary transition-colors">+918169312050 (WhatsApp Only)</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <form className="space-y-4" onSubmit={handleSubmit}>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-foreground mb-1 block">First Name</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                        placeholder="John"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-foreground mb-1 block">Last Name</label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                        placeholder="Doe"
                                        required
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-foreground mb-1 block">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                    placeholder="john@example.com"
                                    required
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-foreground mb-1 block">Message</label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all min-h-[120px]"
                                    placeholder="How can we help?"
                                    required
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={status === 'sending'}
                                className="w-full py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {status === 'sending' ? 'Sending...' : 'Send Message'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
